import { withSacredImages } from "../utils/image-urls";

/** @type {Array<{id:string,name:string,beginner:Object,deep:Object}>} */
const FESTIVALS_RAW = [
  {
    id: "ugadi",
    name: "Ugadi / Yugādi",
    beginner: {
      meaning: "Telugu lunar new year — fresh start aligned with Chaitra śukla pratipad.",
      why_celebrated: "Time is renewed; bitter-sweet ugadi pachadi teaches life's mixed flavors.",
      key_mantra: "ॐ नमो नारायणाय",
      simple_puja: "Neem-jaggery pachadi, new year reading (panchanga), nāma japa at sunrise.",
    },
    deep: {
      origin_story: "Yugādi marks creation's cyclic turn; kings listened to new year's śāstric forecast.",
      scriptural_refs: ["Sūrya Siddhānta calendrics", "Regional nīti-śāstra on Yugādi"],
      puja_vidhi: ["Oil bath before sunrise", "Offer neem flowers to deity", "Listen to pañchāṅga for the year", "Share meal with family"],
      sadhana: ["Set one dharmic intention for the year", "Forgive one old grudge"],
      mantras: ["Viṣṇu nāma", "Gaṇeśa invocation before reading"],
      do_and_dont: ["Do: begin accounts and studies auspiciously", "Don't: quarrel on new year day"],
      food_rules: ["Ugadi pachadi — six tastes", "Sattvic meal after pūjā"],
      charity: ["Annadāna at temple"],
      astrological_notes: ["Chaitra śukla pratipad; avoid adhika māsa for main celebration"],
    },
  },
  {
    id: "rama_navami",
    name: "Rāma Navamī",
    beginner: {
      meaning: "Birth of Lord Rāma — dharma incarnate.",
      why_celebrated: "To remember maryādā, truth, and ideal kingship.",
      key_mantra: "श्री राम जय राम जय जय राम",
      simple_puja: "Midday pūjā, Rāma nāma, read birth chapter of Rāmāyaṇa.",
    },
    deep: {
      origin_story: "Daśaratha's yajña; Rāma born with brothers in Ayodhyā.",
      scriptural_refs: ["Vālmīki Rāmāyaṇa Bāla Kāṇḍa", "Bhāgavata avatāra list"],
      puja_vidhi: ["Midday abhiṣeka", "Offer tulsi", "Procession if community allows", "Rāma paṭṭābhiṣeka meditation"],
      sadhana: ["One chapter Rāmāyaṇa daily for nine days", "Speak truthfully all day"],
      mantras: ["Rāma nāma", "Rāma Rakṣā stotra"],
      do_and_dont: ["Do: honor parents", "Don't: harsh speech"],
      food_rules: ["Many observe partial fast till midday pūjā", "Fruits and milk acceptable"],
      charity: ["Feed children", "Donate Rāmāyaṇa books"],
      astrological_notes: ["Chaitra śukla navamī; Sun strong"],
    },
  },
  {
    id: "nrisimha_jayanti",
    name: "Nṛsiṃha Jayantī",
    beginner: {
      meaning: "Appearance of Narasiṃha avatāra for Prahlāda.",
      why_celebrated: "Faith protected beyond all clever boons of ego.",
      key_mantra: "ॐ उग्रं वीरं महाविष्णुं ज्वलन्तं सर्वतोमुखम्",
      simple_puja: "Evening lamp, Prahlāda story, Narasiṃha stuti.",
    },
    deep: {
      origin_story: "Hiraṇyakaśipu's boons broken at twilight threshold.",
      scriptural_refs: ["Bhāgavata Canto 7"],
      puja_vidhi: ["Sandhyā timing emphasis", "Offer flowers avoiding harsh items per local custom", "Read Prahlāda charitra"],
      sadhana: ["Courage for righteous action", "Protect someone vulnerable"],
      mantras: ["Narasiṃha anuṣṭubh", "Ugra/Śānta forms per lineage"],
      do_and_dont: ["Do: stand with the innocent", "Don't: misuse power"],
      food_rules: ["Fast till sandhyā in many homes"],
      charity: ["Support dharma education"],
      astrological_notes: ["Vaiśākha śukla chaturdaśī"],
    },
  },
  {
    id: "krishna_janmashtami",
    name: "Kṛṣṇa Janmāṣṭamī",
    beginner: {
      meaning: "Birth of Kṛṣṇa at midnight in Mathurā.",
      why_celebrated: "God enters history as joy and teacher.",
      key_mantra: "ॐ नमो भगवते वासुदेवाय",
      simple_puja: "Fast till midnight, cradle worship, butter offering, kīrtan.",
    },
    deep: {
      origin_story: "Vasudeva carries infant across Yamunā; Kamsa defeated.",
      scriptural_refs: ["Bhāgavata 10th canto", "Hari Vaṃśa"],
      puja_vidhi: ["Fasting day", "Midnight abhiṣeka of Bāla Kṛṣṇa", "Swing (jhūlā) seva", "Break fast after pūjā"],
      sadhana: ["Gītā verse meditation", "Kīrtan through the night"],
      mantras: ["Gopāla mantra", "Hare Kṛṣṇa mahā-mantra in saṅga"],
      do_and_dont: ["Do: childlike joy without folly", "Don't: break fast before midnight pūjā"],
      food_rules: ["No grains on many traditions until midnight", "Milk sweets after pūjā"],
      charity: ["Annadāna", "Support gaushālās ethically"],
      astrological_notes: ["Bhādrapada/Śrāvaṇa kṛṣṇa aṣṭamī per region"],
    },
  },
  {
    id: "maha_shivaratri",
    name: "Mahā Śivarātri",
    beginner: {
      meaning: "Great night of Śiva — union of śakti and śiva.",
      why_celebrated: "Śiva drank poison and wed Pārvatī; seekers stay awake in his name.",
      key_mantra: "ॐ नमः शिवाय",
      simple_puja: "Four kāla abhiṣeka or one night japa, bilva, milk on liṅga.",
    },
    deep: {
      origin_story: "Neelakantha, marriage night, liṅga appearance as jyotirliṅga.",
      scriptural_refs: ["Śiva Purāṇa", "Liṅga Purāṇa"],
      puja_vidhi: ["Four praharas of worship", "Bilva archana", "Rudram chanting", "Jaagaran (vigil)"],
      sadhana: ["Pañcākṣarī through the night", "Mauna hours"],
      mantras: ["Mahāmṛtyuñjaya", "Rudram"],
      do_and_dont: ["Do: stay sattvic", "Don't: sleep through without intention"],
      food_rules: ["Fruit/milk fast common", "Break after sunrise pūjā"],
      charity: ["Offer bilva to temples", "Feed devotees"],
      astrological_notes: ["Māgha/Phālguna kṛṣṇa chaturdaśī"],
    },
  },
  {
    id: "navaratri",
    name: "Navarātri",
    beginner: {
      meaning: "Nine nights honoring the Divine Mother.",
      why_celebrated: "Durgā's victory and renewal of śakti in the devotee.",
      key_mantra: "ॐ दुं दुर्गायै नमः",
      simple_puja: "Daily lamp, kumkum, Devi stuti, avoid tamasic food if fasting.",
    },
    deep: {
      origin_story: "Nine nights of battle with asuras; tenth is Vijayādaśamī.",
      scriptural_refs: ["Devī Māhātmyam", "Markandeya Purāṇa"],
      puja_vidhi: ["Ghata sthāpana day 1", "Kumārī pūjā", "Aṣṭamī homa in traditions", "Ayudha pūjā"],
      sadhana: ["Saptaśatī pārāyaṇa with teacher", "Fasting per capacity"],
      mantras: ["Argalā stotram", "Navārṇa with dikṣā only"],
      do_and_dont: ["Do: respect women", "Don't: neglect hygiene in fasting"],
      food_rules: ["Phalāhāra or single meal per vow"],
      charity: ["Annadāna nightly"],
      astrological_notes: ["Āśvina śukla pratipad through navamī"],
    },
  },
  {
    id: "vijayadashami",
    name: "Vijayādaśamī",
    beginner: {
      meaning: "Tenth day of victory — Rāma's return and Durgā's triumph.",
      why_celebrated: "Begin learning, travel, and righteous ventures.",
      key_mantra: "श्री राम जय राम",
      simple_puja: "Worship weapons/tools as symbols of skill, start studies, exchange leaves.",
    },
    deep: {
      origin_story: "Rāvaṇa defeated; Śami tree story; students honor gurus.",
      scriptural_refs: ["Rāmāyaṇa Yuddha Kāṇḍa", "Devī Māhātmyam finale"],
      puja_vidhi: ["Ayudha pūjā", "Vidyāraṃbha for children", "Procession in communities"],
      sadhana: ["Begin one scripture or skill", "Resolve one inner enemy habit"],
      mantras: ["Rāma nāma", "Durgā stuti"],
      do_and_dont: ["Do: act with dharma", "Don't: start adharmic work"],
      food_rules: ["Feast after morning pūjā"],
      charity: ["Support teachers"],
      astrological_notes: ["Āśvina śukla daśamī"],
    },
  },
  {
    id: "vaikuntha_ekadashi",
    name: "Vaikuṇṭha Ekādaśi",
    beginner: {
      meaning: "Sacred Ekādaśī opening the gate of Viṣṇu's abode.",
      why_celebrated: "Intensified Viṣṇu bhakti and fasting merit.",
      key_mantra: "ॐ नमो नारायणाय",
      simple_puja: "Ekādaśī fast, Viṣṇu Sahasranāma, stay awake near deity if able.",
    },
    deep: {
      origin_story: "Pāṇḍavas and Bhīṣma traditions link Ekādaśī to Hari.",
      scriptural_refs: ["Padma Purāṇa Ekādaśī mahātmya", "Bhāgavata"],
      puja_vidhi: ["Full or partial fast", "Tulsi offering", "Vaikuṇṭha dvāra seva in temples"],
      sadhana: ["Viṣṇu Sahasranāma", "Silence and charity"],
      mantras: ["Nārāyaṇa mantra", "Daśākṣarī"],
      do_and_dont: ["Do: break fast only on dvādaśī", "Don't: eat grains on Ekādaśī"],
      food_rules: ["Strict Ekādaśī rules — no grains, many avoid beans"],
      charity: ["Annadāna on dvādaśī"],
      astrological_notes: ["Kārttika/Phālguna śukla ekādaśī per lineage"],
    },
  },
  {
    id: "kartika_deepam",
    name: "Kārttika Dīpam",
    beginner: {
      meaning: "Festival of lamps in Kārttika — Śiva as column of light.",
      why_celebrated: "Light dispels inner darkness; full moon of Kārttika is auspicious.",
      key_mantra: "ॐ नमः शिवाय",
      simple_puja: "Light rows of lamps at dusk, visit Śiva temple if possible.",
    },
    deep: {
      origin_story: "Arunachala as jyotirliṅga; lamps symbolize jñāna.",
      scriptural_refs: ["Skanda Purāṇa on Kārttika", "Arunachala sthala purāṇa"],
      puja_vidhi: ["Deepotsava", "Circumambulation of hill or temple", "Annadāna"],
      sadhana: ["Lamp meditation at dusk", "Kārttika month daily lamp"],
      mantras: ["Śiva pañcākṣarī", "Arunachala śaranam"],
      do_and_dont: ["Do: reduce anger", "Don't: waste oil carelessly"],
      food_rules: ["Many avoid non-veg in Kārttika month"],
      charity: ["Oil for temple lamps"],
      astrological_notes: ["Kārttika pūrṇimā"],
    },
  },
  {
    id: "ganesh_chaturthi",
    name: "Gaṇeśa Chaturthi",
    beginner: {
      meaning: "Birthday of Gaṇeśa — lord of auspicious beginnings.",
      why_celebrated: "Community welcomes Gaṇeśa into homes before visarjan.",
      key_mantra: "ॐ गं गणपतये नमः",
      simple_puja: "Install idol, daily modaka offering, aarti, visarjan with gratitude.",
    },
    deep: {
      origin_story: "Pārvatī's son restored by Śiva; worshipped before all ventures.",
      scriptural_refs: ["Gaṇeśa Purāṇa", "Mudgala Purāṇa"],
      puja_vidhi: ["Prāṇa pratiṣṭhā", "Daily āratī", "Visarjan procession eco-friendly"],
      sadhana: ["21 modaka offerings metaphorically as 21 days discipline", "Study without obstacle"],
      mantras: ["Gaṇeśa atharvaśīrṣa", "Vakratunda stotra"],
      do_and_dont: ["Do: eco-friendly idols", "Don't: pollute water bodies"],
      food_rules: ["Modaka prasāda", "Sattvic feasts"],
      charity: ["Support artisans", "Tree planting after visarjan"],
      astrological_notes: ["Bhādrapada śukla chaturthī"],
    },
  },
];

export const FESTIVALS = withSacredImages("festivals", FESTIVALS_RAW);

export function getFestival(id) {
  return FESTIVALS.find((f) => f.id === id) ?? null;
}
